import React, { useEffect, useState } from "react";
import { fetchAuthedBlobUrl } from "@/lib/api";
import { cn } from "@/lib/utils";
import { FileText, ImageOff } from "lucide-react";

/**
 * <AuthenticatedImage src="/api/uploads/xxx.png" />
 *
 * Fetches the image with the current Bearer token attached and displays it
 * as a blob. Falls back to a placeholder if the fetch fails (e.g. PDF).
 */
export const AuthenticatedImage = ({ src, alt, className, testId }) => {
    const [state, setState] = useState({ url: null, err: false });

    useEffect(() => {
        let alive = true;
        let created = null;
        if (!src) return () => {};
        fetchAuthedBlobUrl(src)
            .then((u) => {
                if (!alive) {
                    URL.revokeObjectURL(u);
                    return;
                }
                created = u;
                setState({ url: u, err: false });
            })
            .catch(() => {
                if (alive) setState({ url: null, err: true });
            });
        return () => {
            alive = false;
            if (created) URL.revokeObjectURL(created);
        };
    }, [src]);

    if (state.err) {
        return (
            <div
                className={cn(
                    "flex h-full min-h-24 w-full items-center justify-center rounded-xl bg-neutral-100 text-neutral-500",
                    className,
                )}
                data-testid={testId || "authed-image-err"}
            >
                {src?.endsWith(".pdf") ? (
                    <FileText size={20} className="mr-2" />
                ) : (
                    <ImageOff size={20} className="mr-2" />
                )}
                <span className="text-xs">Preview unavailable</span>
            </div>
        );
    }
    if (!state.url) {
        return (
            <div
                className={cn(
                    "h-full min-h-24 w-full animate-pulse rounded-xl bg-neutral-200",
                    className,
                )}
                data-testid={testId || "authed-image-loading"}
            />
        );
    }
    return (
        <img
            src={state.url}
            alt={alt || ""}
            className={cn("h-full w-full rounded-xl object-cover", className)}
            data-testid={testId || "authed-image"}
        />
    );
};

export default AuthenticatedImage;
